import type { RegistryItem } from "shadcn/schema";

export const hooks: RegistryItem[] = [
  {
    description:
      "Gone: the page's one AudioContext is the webAudio of the web-audio lib, imported where it is used. Installs that lib.",
    files: [],
    name: "use-web-audio",
    registryDependencies: ["@audio-ui/web-audio"],
    type: "registry:hook",
  },
  {
    description:
      "Renamed to use-simulated-clock: installs it. It is a clock for a timeline that makes no sound, not one on the audio clock.",
    files: [],
    name: "use-playback-clock",
    registryDependencies: ["@audio-ui/use-simulated-clock"],
    type: "registry:hook",
  },
  {
    categories: ["audio", "timeline"],
    dependencies: ["@audio-ui/react"],
    description:
      "A clock for a timeline that makes no sound: it runs while playing, loops between two points, and the playhead follows it without re-rendering React. Not on the audio clock: use use-transport-clock to schedule sound",
    files: [
      {
        path: "hooks/use-simulated-clock.ts",
        type: "registry:hook",
      },
    ],
    name: "use-simulated-clock",
    registryDependencies: [],
    type: "registry:hook",
  },
  {
    categories: ["audio", "timeline"],
    dependencies: [],
    description:
      "A selection rectangle for the clips of a timeline: drag across bare lane (with Shift, or in a multi-select mode) and the clips it touches join the selection",
    files: [
      {
        path: "hooks/use-marquee-selection.ts",
        type: "registry:hook",
      },
    ],
    name: "use-marquee-selection",
    registryDependencies: [],
    type: "registry:hook",
  },
  {
    categories: ["audio", "timeline"],
    dependencies: ["@audio-ui/react"],
    description:
      "The state of an arrangement of clips on tracks, with its operations: selection, group moves and copies, split, duplicate, delete, adding and removing tracks, per-track mix and height, and undo and redo over tracks and clips",
    files: [
      {
        path: "hooks/use-arrangement.ts",
        type: "registry:hook",
      },
      {
        path: "lib/arrangement-state.ts",
        type: "registry:lib",
      },
    ],
    name: "use-arrangement",
    registryDependencies: ["@audio-ui/arrangement", "@audio-ui/undo-history"],
    type: "registry:hook",
  },
  {
    categories: ["audio", "timeline", "piano"],
    dependencies: ["@audio-ui/core", "@audio-ui/react"],
    description:
      "Plays an arrangement's MIDI clips while it plays: notes scheduled ahead on the audio clock, each MIDI track through a channel whose gain and pan follow its mix or its automation, and the notes a clip's editor auditions.",
    files: [
      {
        path: "hooks/use-midi-playback.ts",
        type: "registry:hook",
      },
    ],
    name: "use-midi-playback",
    registryDependencies: [
      "@audio-ui/arrangement",
      "@audio-ui/use-master-bus",
      "@audio-ui/use-synth-voices",
      "@audio-ui/envelope-ramp",
      "@audio-ui/web-audio",
    ],
    type: "registry:hook",
  },
  {
    categories: ["timeline", "mixer"],
    description:
      "Reorders tracks: drag a track's grip to move it, with the view scrolling at its edges and a copy of its header under the pointer, or press Up and Down on the grip. With the grip, the copy and the line where the track would land.",
    files: [
      {
        path: "hooks/use-track-reorder.ts",
        type: "registry:hook",
      },
      {
        path: "audio/elements/track-grip.tsx",
        type: "registry:ui",
      },
    ],
    name: "use-track-reorder",
    registryDependencies: ["@audio-ui/sortable-drag"],
    type: "registry:hook",
  },
  {
    categories: ["timeline"],
    description:
      "The zoom of an arrangement's timeline: it fits the view beside the track headers until it is zoomed, zooms around a time that stays put on screen, fits a stretch of time, steps back through the zooms it left, and keeps its bars when the tempo changes.",
    files: [
      {
        path: "hooks/use-timeline-zoom.ts",
        type: "registry:hook",
      },
    ],
    name: "use-timeline-zoom",
    type: "registry:hook",
  },
  {
    categories: ["timeline"],
    dependencies: ["@audio-ui/react"],
    description:
      "A stretch of time on some tracks of an arrangement, drawn by dragging across bare lanes, with its overlay and what the keyboard does with it: copy, cut, paste, duplicate, consolidate, loop, fade, delete and open time, each one step of undo.",
    files: [
      {
        path: "hooks/use-time-selection.ts",
        type: "registry:hook",
      },
      {
        path: "audio/elements/time-selection.tsx",
        type: "registry:ui",
      },
    ],
    name: "use-time-selection",
    registryDependencies: [
      "@audio-ui/arrangement",
      "@audio-ui/use-arrangement",
    ],
    type: "registry:hook",
  },
  {
    categories: ["timeline"],
    dependencies: ["@audio-ui/core", "@audio-ui/react"],
    description:
      "What the keyboard does with an arrangement's cursor: the arrows on the grid, Home and End, Up and Down to pick a track, Alt and the arrows to jump between locators and clip edges, M to add a locator.",
    files: [
      {
        path: "hooks/use-timeline-cursor.ts",
        type: "registry:hook",
      },
    ],
    name: "use-timeline-cursor",
    registryDependencies: ["@audio-ui/arrangement"],
    type: "registry:hook",
  },
  {
    categories: ["timeline"],
    dependencies: ["@audio-ui/core"],
    description:
      "The grid of an arrangement's timeline: adaptive to the zoom or a fixed division, straight or triplet, snapping or not, its name for a menu button, and the changes Cmd/Ctrl + 1 to 5 make.",
    files: [
      {
        path: "hooks/use-timeline-grid.ts",
        type: "registry:hook",
      },
    ],
    name: "use-timeline-grid",
    type: "registry:hook",
  },
  {
    categories: ["audio", "sequencer", "piano"],
    description:
      "A look-ahead transport clock on the audio clock: it calls back with the audio time of each step so notes are scheduled sample-accurately, with a loop region, seek and tempo changes.",
    files: [
      {
        path: "hooks/use-transport-clock.ts",
        type: "registry:hook",
      },
    ],
    name: "use-transport-clock",
    dependencies: ["@audio-ui/core"],
    registryDependencies: ["@audio-ui/web-audio"],
    type: "registry:hook",
  },
  {
    categories: ["audio", "piano", "sequencer"],
    dependencies: ["@audio-ui/core", "@audio-ui/react"],
    description:
      "Plays a piano roll's notes, which you own: play, pause and stop, a loop or once through, a metronome, seeking, and the notes the roll auditions, with a playhead for the timeline and a clock for the transport.",
    files: [
      {
        path: "hooks/use-piano-roll-playback.ts",
        type: "registry:hook",
      },
    ],
    name: "use-piano-roll-playback",
    registryDependencies: [
      "@audio-ui/drum-synthesis",
      "@audio-ui/use-transport-clock",
      "@audio-ui/use-synth-voices",
      "@audio-ui/envelope-ramp",
      "@audio-ui/web-audio",
    ],
    type: "registry:hook",
  },
  {
    categories: ["timeline", "piano", "envelope"],
    description:
      "Undo and redo over a value you own: an edit made in one go, or a gesture that undoes as one.",
    files: [
      {
        path: "hooks/use-undo-history.ts",
        type: "registry:hook",
      },
    ],
    name: "use-undo-history",
    registryDependencies: ["@audio-ui/undo-history"],
    type: "registry:hook",
  },
  {
    categories: ["audio", "synth", "sequencer"],
    description:
      "A master output bus with a safety limiter: every source connects into one gain node that a single fader governs.",
    files: [
      {
        path: "hooks/use-master-bus.ts",
        type: "registry:hook",
      },
    ],
    name: "use-master-bus",
    registryDependencies: ["@audio-ui/web-audio"],
    type: "registry:hook",
  },
  {
    categories: ["audio", "synth", "piano", "envelope"],
    description:
      "Polyphonic synth voices on the Web Audio API: start and release notes with an envelope, a velocity and a pitch bend.",
    files: [
      {
        path: "hooks/use-synth-voices.ts",
        type: "registry:hook",
      },
    ],
    name: "use-synth-voices",
    dependencies: ["@audio-ui/core"],
    registryDependencies: ["@audio-ui/envelope-ramp"],
    type: "registry:hook",
  },
  {
    categories: ["audio", "piano", "synth"],
    dependencies: ["@audio-ui/core"],
    description:
      "The notes of the page's MIDI input, with velocity and pitch bend: every component that calls it hears the same devices.",
    files: [
      {
        path: "hooks/use-midi-input.ts",
        type: "registry:hook",
      },
    ],
    name: "use-midi-input",
    registryDependencies: [],
    type: "registry:hook",
  },
  {
    categories: ["audio", "piano", "synth"],
    description:
      "The computer's keyboard as a keyboard of notes, as in a DAW: the middle row from A plays the white keys and the row above the black ones, by where the keys sit; Z and X move an octave, C and V the velocity.",
    files: [
      {
        path: "hooks/use-computer-keyboard.ts",
        type: "registry:hook",
      },
      {
        path: "lib/computer-keyboard.ts",
        type: "registry:lib",
      },
    ],
    name: "use-computer-keyboard",
    registryDependencies: [],
    type: "registry:hook",
  },
  {
    categories: ["audio", "waveform"],
    dependencies: ["@audio-ui/core"],
    description:
      "An AnalyserNode as live signals for a waveform: the loudest sample of the moment, frequency bands and the raw samples, read every frame without re-rendering",
    files: [
      {
        path: "hooks/use-audio-signal.ts",
        type: "registry:hook",
      },
    ],
    name: "use-audio-signal",
    registryDependencies: [],
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio"],
    description:
      "The sound a device plays: the one chosen in its session, such as from a preview's bar, else the one that shows best what the device does. It tells the session its default, so a bar can offer the choice only where there is one. Outside a session it is always the default.",
    files: [
      {
        path: "hooks/use-demo-source.ts",
        type: "registry:hook",
      },
    ],
    name: "use-demo-source",
    registryDependencies: [
      "@audio-ui/demo-sound",
      "@audio-ui/use-audio-session",
    ],
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio"],
    description:
      "A session of its own for a demo and its controls: a play switch and a microphone switch that nothing outside it follows. Holds no audio, so two previews on a page never play together. Outside a session the switches stay off.",
    files: [
      {
        path: "hooks/use-audio-session.tsx",
        type: "registry:hook",
      },
    ],
    name: "use-audio-session",
    registryDependencies: [],
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio"],
    description:
      "The play switch of the nearest session: a button in its bar flips it, every component inside the session that reads it follows. Holds no audio, so a demo can leave its play button to its host.",
    files: [
      {
        path: "hooks/use-play-switch.ts",
        type: "registry:hook",
      },
    ],
    name: "use-play-switch",
    registryDependencies: ["@audio-ui/use-audio-session"],
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio", "tuner"],
    description:
      "The microphone as an AnalyserNode on the shared AudioContext: raw signal, never connected to the speakers, released on stop and on unmount. The session's switch, usePageMicrophone, lets a button turn it on for a demo",
    files: [
      {
        path: "hooks/use-microphone.ts",
        type: "registry:hook",
      },
    ],
    name: "use-microphone",
    registryDependencies: [
      "@audio-ui/web-audio",
      "@audio-ui/use-audio-session",
    ],
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio", "tuner"],
    dependencies: ["@audio-ui/react", "@audio-ui/core"],
    description:
      "The pitch of a signal as a live source: the note nearest the sound and its offset in cents, steadied by a median and held a moment after the sound stops, with no React render per reading",
    files: [
      {
        path: "hooks/use-pitch.ts",
        type: "registry:hook",
      },
    ],
    name: "use-pitch",
    registryDependencies: [],
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio", "meter"],
    dependencies: ["@audio-ui/react", "@audio-ui/core"],
    description:
      "The live levels of a signal from its AnalyserNodes, in the shape a Meter paints from: RMS in dB per channel with a peak that holds and falls, refreshed every frame without re-rendering",
    files: [
      {
        path: "hooks/use-audio-levels.ts",
        type: "registry:hook",
      },
    ],
    name: "use-audio-levels",
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio", "meter"],
    dependencies: [],
    description:
      "Levels that move on their own, with a peak that holds and falls, in the shape a Meter paints from: a demo meter that is never at rest",
    files: [
      {
        path: "hooks/use-simulated-levels.ts",
        type: "registry:hook",
      },
    ],
    name: "use-simulated-levels",
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio", "plot"],
    dependencies: ["@audio-ui/react", "@audio-ui/core"],
    description:
      "The spectrum of an AnalyserNode as a live source for Plot.Trace: dB per point on a logarithmic frequency axis, refreshed every frame without re-rendering, and its held peaks as a second source",
    files: [
      {
        path: "hooks/use-spectrum.ts",
        type: "registry:hook",
      },
    ],
    name: "use-spectrum",
    registryDependencies: [],
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio", "plot"],
    dependencies: ["@audio-ui/core"],
    description:
      "Pink noise played through an equalizer that follows your bands, with the analyser after it: what a plot draws is what is heard",
    files: [
      {
        path: "hooks/use-noise-through-eq.ts",
        type: "registry:hook",
      },
    ],
    name: "use-noise-through-eq",
    registryDependencies: [
      "@audio-ui/demo-sound",
      "@audio-ui/use-play-switch",
      "@audio-ui/web-audio",
    ],
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio", "device"],
    dependencies: ["@audio-ui/core"],
    description:
      "A short loop of drums and plucked notes played through an effect's engine and out to a destination, for a block to show what a device does: it plays while the session's play switch is on, the engine follows the state, and an analyser of the output is there for a spectrum.",
    files: [
      {
        path: "hooks/use-device-loop.ts",
        type: "registry:hook",
      },
    ],
    name: "use-device-loop",
    registryDependencies: [
      "@audio-ui/demo-sound",
      "@audio-ui/use-demo-source",
      "@audio-ui/use-play-switch",
      "@audio-ui/web-audio",
    ],
    type: "registry:hook",
  },
  {
    categories: ["hooks", "audio", "plot"],
    dependencies: ["@audio-ui/react"],
    description:
      "A looping pulse played through a compressor that follows your controls, with the level going in as a live source in dB: pair it with a transfer curve's Plot.Dot",
    files: [
      {
        path: "hooks/use-pulses-through-compressor.ts",
        type: "registry:hook",
      },
    ],
    name: "use-pulses-through-compressor",
    registryDependencies: [
      "@audio-ui/demo-sound",
      "@audio-ui/use-play-switch",
      "@audio-ui/web-audio",
    ],
    type: "registry:hook",
  },
];
